import { memo, useCallback, useEffect, useRef, useState } from "react";

import {
  Badge,
  GenerateButton,
  ItemCard,
  ModelName,
  PinBadge,
  StyledImage,
  StyledVideo,
  Username,
  VideoFooter,
  ThumbnailImageSmall,
  ThumbnailSkeleton,
  DoubleThumbContainer,
  DoubleThumbImage,
  DoubleThumbBadge,
  DoubleThumbSkeleton,
  // RateNumber,
  // RateContainer,
  // RateIcon,
} from "./ExploreItem.styles";
import {
  BLEND_MODEL,
  CREATE_IMAGE_MODEL_NAME,
  MEDIA_TYPE,
  QWEN_Z_TWO_INPUT_IMAGES,
  QWEN_Z_THREE_INPUT_IMAGES,
} from "../../utils/constants";

import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import { selectAutoPlay } from "../../store/slices/common/slice";
import { formatModelName } from "../../utils/utils";
import { useMediaQuery } from "react-responsive";
import { ReactComponent as ChristmasCap } from "../../assets/icons/christmasCap.svg";
import { ReactComponent as PinIcon } from "../../assets/icons/pin.svg";

const LONG_PRESS_MS = 600;

const ExploreItem = ({
  item,
  onItemClick,
  onItemLongPress,
  onItemContextMenu,
  isPinned,
  hideUsername,
  extraContent,
  offGenerate,
  // hideRate,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const autoplayEnabled = useSelector(selectAutoPlay);
  const videoRef = useRef();
  const itemCardRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const suppressGenerateUntilLeaveRef = useRef(false);

  const [inputUrl, setInputUrl] = useState(null);
  const [resizedThumb, setResizedThumb] = useState(null);
  const [resizedThumb2, setResizedThumb2] = useState(null);

  const [outputUrl, setOutputUrl] = useState(null);
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(false);

  const isDoubleInputImageModel =
    item?.modelType === QWEN_Z_TWO_INPUT_IMAGES ||
    item?.modelType === BLEND_MODEL;
  const isTripleInputImageModel = item?.modelType === QWEN_Z_THREE_INPUT_IMAGES;

  const isCreateImageModel = item?.modelType === CREATE_IMAGE_MODEL_NAME;

  const { ref: containerRef, inView } = useInView({
    threshold: 0.01,
    rootMargin: "300px",
    triggerOnce: true,
  });

  const { ref: autoplayRef, inView: isInAutoplayView } = useInView({
    threshold: 0.5,
    rootMargin: "0px",
  });

  useEffect(() => {
    if (!inView) return;
    setInputUrl(
      (prev) =>
        prev ||
        item?.input?.image?.resizedImage?.url ||
        item?.input?.image?.url,
    );
    setResizedThumb(
      (prev) =>
        prev ||
        item?.input?.image?.url ||
        item?.input?.image?.resizedImage?.url,
    );
    setResizedThumb2(
      (prev) =>
        prev ||
        item?.input?.image?.resizedImage?.url2 ||
        item?.input?.image?.url,
    );
    setOutputUrl(
      (prev) =>
        prev ||
        (item.output.type === MEDIA_TYPE.IMAGE
          ? item?.output?.image?.url
          : item?.output?.video?.compressedUrl || item?.output?.video?.url),
    );
  }, [inView, item]);

  useEffect(() => {
    if (
      !videoRef.current ||
      !outputUrl ||
      item.output.type !== MEDIA_TYPE.VIDEO
    )
      return;

    const video = videoRef.current;
    if (video.src !== outputUrl) {
      video.src = outputUrl;
      video.load();
    }
  }, [outputUrl, item.output.type]);

  const handleVideoHover = useCallback(() => {
    if (videoRef.current && videoRef.current.readyState >= 2) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const handleVideoLeave = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  const ensureInlinePlayback = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;

    element.muted = true;
    element.defaultMuted = true;
    element.playsInline = true;
    element.loop = true;
    element.preload = "none";
    element.setAttribute("muted", "");
    element.setAttribute("playsinline", "");
    element.setAttribute("webkit-playsinline", "");
    element.setAttribute("loop", "");
  }, []);

  useEffect(() => {
    if (!videoRef.current) return;

    ensureInlinePlayback();

    const shouldPlay = autoplayEnabled && isInAutoplayView;

    if (shouldPlay) {
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((error) => {
          console.log(error);
        });
      }
    } else {
      videoRef.current.pause();
      //   videoRef.current.currentTime = 0;
    }
  }, [ensureInlinePlayback, autoplayEnabled, isInAutoplayView, outputUrl]);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const startLongPress = useCallback(() => {
    if (!onItemLongPress) return;
    cancelLongPress();
    longPressFiredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      suppressGenerateUntilLeaveRef.current = true;
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      setIsHovered(false);
      onItemLongPress(itemCardRef.current?.getBoundingClientRect() || null);
    }, LONG_PRESS_MS);
  }, [onItemLongPress, cancelLongPress]);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    hoverTimerRef.current = setTimeout(() => {
      if (suppressGenerateUntilLeaveRef.current) return;
      setIsHovered(true);
    }, 200);
    startLongPress();
  };

  const handleTouchMove = (e) => {
    const diffY = Math.abs(e.touches[0].clientY - touchStartY.current);
    if (diffY > 10) cancelLongPress();
  };

  const handleTouchEnd = (e) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    cancelLongPress();
    setIsHovered(false);

    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }

    const diffY = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    const pressDuration = Date.now() - touchStartTime.current;

    // only count as tap if short (<500 ms) and not a scroll
    if (diffY < 10 && pressDuration < 500) {
      setTimeout(() => {
        onItemClick?.();
      }, 200);
    }
  };

  const handleTouchCancel = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    cancelLongPress();
    setIsHovered(false);
  };

  const handleClick = () => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onItemClick?.();
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (isMobile) return;
    suppressGenerateUntilLeaveRef.current = true;
    setIsHovered(false);
    onItemContextMenu?.(itemCardRef.current?.getBoundingClientRect() || null);
  };

  return (
    <ItemCard
      id={item._id}
      ref={(el) => {
        itemCardRef.current = el;
        containerRef(el);
        autoplayRef(el);
      }}
      onContextMenu={handleContextMenu}
      onClick={!isMobile ? handleClick : undefined}
      onMouseDown={!isMobile ? startLongPress : undefined}
      onMouseUp={!isMobile ? cancelLongPress : undefined}
      style={{
        aspectRatio:
          item?.input?.image?.resizedImage?.width /
          item?.input?.image?.resizedImage?.height,
      }}
      onMouseEnter={() => {
        if (!offGenerate) {
          if (!isMobile) {
            if (suppressGenerateUntilLeaveRef.current) return;
            setIsHovered(true);
            if (!autoplayEnabled) {
              handleVideoHover();
            }
          }
        } else {
          handleVideoHover();
        }
      }}
      onMouseLeave={() => {
        cancelLongPress();
        suppressGenerateUntilLeaveRef.current = false;
        if (!offGenerate) {
          if (!isMobile) {
            setIsHovered(false);
            if (!autoplayEnabled) {
              handleVideoLeave();
            }
          }
        } else {
          handleVideoLeave();
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    >
      {/* {!hideRate && (
        <RateContainer>
          <RateIcon />
          <RateNumber>{item?.modelRating?.toFixed(1) || "5.0"}</RateNumber>
        </RateContainer>
      )} */}
      {isPinned && (
        <PinBadge aria-label="Pinned post">
          <PinIcon />
        </PinBadge>
      )}

      {!isThumbnailLoaded &&
        !isCreateImageModel &&
        !isDoubleInputImageModel &&
        !isTripleInputImageModel && <ThumbnailSkeleton />}

      {!isThumbnailLoaded && isDoubleInputImageModel && (
        <>
          <DoubleThumbSkeleton $index={1} />
          <DoubleThumbSkeleton $index={2} />
        </>
      )}

      {!isThumbnailLoaded && isTripleInputImageModel && (
        <>
          <DoubleThumbSkeleton $index={1} />
          <DoubleThumbSkeleton $index={2} />
          <DoubleThumbSkeleton $index={3} />
        </>
      )}

      {inputUrl &&
        !isDoubleInputImageModel &&
        !isTripleInputImageModel &&
        !isCreateImageModel && (
          <ThumbnailImageSmall
            src={inputUrl}
            alt={`${formatModelName(item.name)} thumbnail`}
            className="small"
            $loaded={isThumbnailLoaded}
            onLoad={() => setIsThumbnailLoaded(true)}
            onError={() => setIsThumbnailLoaded(true)}
            draggable={false}
          />
        )}

      {(isDoubleInputImageModel || isTripleInputImageModel) && inputUrl && (
        <DoubleThumbContainer $index={1}>
          <DoubleThumbImage
            src={inputUrl}
            alt={`${formatModelName(item.name)} thumbnail 1`}
            $loaded={isThumbnailLoaded}
            onLoad={() => setIsThumbnailLoaded(true)}
            onError={() => setIsThumbnailLoaded(true)}
            draggable={false}
          />
          {isThumbnailLoaded && <DoubleThumbBadge>1</DoubleThumbBadge>}
        </DoubleThumbContainer>
      )}

      {(isDoubleInputImageModel || isTripleInputImageModel) && resizedThumb && (
        <DoubleThumbContainer $index={2}>
          <DoubleThumbImage
            src={resizedThumb}
            alt={`${formatModelName(item.name)} thumbnail 2`}
            $loaded={isThumbnailLoaded}
            onLoad={() => setIsThumbnailLoaded(true)}
            onError={() => setIsThumbnailLoaded(true)}
            draggable={false}
          />
          {isThumbnailLoaded && <DoubleThumbBadge>2</DoubleThumbBadge>}
        </DoubleThumbContainer>
      )}

      {isTripleInputImageModel && (inputUrl || resizedThumb2) && (
        <DoubleThumbContainer $index={3}>
          <DoubleThumbImage
            src={resizedThumb2 || inputUrl}
            alt={`${formatModelName(item.name)} thumbnail 3`}
            $loaded={isThumbnailLoaded}
            onLoad={() => setIsThumbnailLoaded(true)}
            onError={() => setIsThumbnailLoaded(true)}
            draggable={false}
          />
          {isThumbnailLoaded && <DoubleThumbBadge>3</DoubleThumbBadge>}
        </DoubleThumbContainer>
      )}
      {item.output.type === MEDIA_TYPE.IMAGE ? (
        <StyledImage
          src={outputUrl}
          draggable={false}
          alt={`${formatModelName(item.name)} image`}
        />
      ) : (
        <StyledVideo ref={videoRef} src={outputUrl} imageSrc={inputUrl} />
      )}

      <GenerateButton className="generate-button" $show={isHovered}>
        Generate
      </GenerateButton>

      <VideoFooter className="video-footer" $hideContent={isHovered}>
        <ModelName>{formatModelName(item.name)}</ModelName>
        {!hideUsername && item.username && (
          <Username>@{item.username}</Username>
        )}
        {((item?.tags && item?.tags?.length > 0) ||
          item.output.videoDuration === 8) && (
          <div style={{ position: "relative", display: "flex", gap: "4px" }}>
            {item?.tags && item?.tags?.length > 0 && (
              <Badge $isMobile={isMobile} $variant={item?.tags[0]?.name}>
                {item?.tags[0]?.name?.toUpperCase()}
              </Badge>
            )}
            {item?.tags[0]?.name === "Santa's Special" && (
              <ChristmasCap
                style={{
                  position: "absolute",
                  left: -8,
                  top: -7,
                  width: "23px",
                  height: "23px",
                }}
              />
            )}
            {item.output.videoDuration === 8 && (
              <Badge $isMobile={isMobile} $variant={"8s"}>
                8S
              </Badge>
            )}

            {item?.tags && item?.tags?.length > 1 && (
              <Badge $isMobile={isMobile} $variant={item?.tags[1]?.name}>
                {item?.tags[1]?.name?.toUpperCase()}
              </Badge>
            )}
          </div>
        )}
      </VideoFooter>

      {extraContent}
    </ItemCard>
  );
};

export default memo(ExploreItem);
